import HBIcon from "@/components/HBIcon";
import VipTag from "@/components/VipTag";
import { isBigCompanyByUser, VIP_RIGHT_IDENTIFICATION } from "@/constants/vip";
import useAppStore from "@/context/store";
import { getCurrentVip, getUserRightCount } from "@/services";
import getTimeDuration from "@/utils/getTimeDuration";
import useRequest from "@/utils/useReq";
import dayjs from "dayjs";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import { Case, Default, Else, If, Switch, Then } from "react-if";
import styles from "./index.module.less";
import { useLocalStorageRedTip, useRedTipState } from "./redTipState";

export function VipCard() {
  const { couponIds, couponTip, orderTip } = useRedTipState(
    ({ couponIds, couponTip, orderTip }) => ({
      couponIds,
      couponTip,
      orderTip,
    })
  );

  const { setReadCouponIds, setReadOrderIds } = useLocalStorageRedTip();

  const { user, unPayOrders } = useAppStore(({ user, unPayOrders }) => ({
    user,
    unPayOrders,
  }));

  const router = useRouter();

  const { data } = useRequest(async () => {
    const vips = await getCurrentVip();
    const effect_vips = vips.filter((vip) => !vip.is_expired);

    if (effect_vips.length) {
      const download = await getUserRightCount([
        { right_code: VIP_RIGHT_IDENTIFICATION },
      ]);

      const vip_expire =
        effect_vips.length &&
        Math.min(...effect_vips.map((r) => new Date(r.expires_at).getTime()));

      return {
        isVip: true,
        almostExipired: dayjs(vip_expire).diff(dayjs(), "day", true) < 3,
        expireTime: getTimeDuration(vip_expire, "", true),
        download: download[0],
      };
    }

    return {
      isVip: false,
    };
  }, []);

  const { isVip, expireTime, almostExipired, download } = data || {
    isVip: user?.extra?.material_vip > 0,
  };

  const isBigCompany = useMemo(() => {
    if (user) {
      return isBigCompanyByUser(user);
    }
  }, [user]);

  // 曾经是会员
  const isVipEver = useMemo(() => {
    return user.extra?.material_vip;
  }, [user]);

  const onCouponClick = useCallback(
    (evt) => {
      evt.stopPropagation();
      // 未使用优惠券设为已读
      setReadCouponIds(couponIds);

      // 取消红点提示
      useRedTipState.setState({
        couponTip: false,
      });
    },
    [couponIds, setReadCouponIds]
  );

  const onOrderClick = useCallback(
    (evt) => {
      evt.stopPropagation();
      // 未支付订单设为已读
      setReadOrderIds(unPayOrders.map((el) => el.id));

      // 取消红点提示
      useRedTipState.setState({
        orderTip: false,
      });
    },
    [setReadOrderIds, unPayOrders]
  );

  const goRight = useCallback(
    (evt) => {
      evt.stopPropagation();
      router.push(isVipEver ? "/me/right" : "/vip/info");
    },
    [isVipEver, router]
  );

  return (
    <div className={styles.card} onClick={goRight}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.title}>
            <span>花瓣素材会员</span>
            <VipTag />
          </div>
          <Switch>
            <Case condition={almostExipired}>
              <div className={styles.subtitle}>
                VIP将在<span>{expireTime}</span>后到期
              </div>
            </Case>
            <Case condition={isVip}>
              <If condition={isBigCompany}>
                <Then>
                  <div className={styles.subtitle}>
                    可下载 <span>{download?.left || 0}</span>/
                    {download?.quantity || 0}
                  </div>
                </Then>
                <Else>
                  <div className={styles.subtitle}>
                    今日可下载 <span>{download?.left || 0}</span>/
                    {download?.quantity || 0}
                  </div>
                </Else>
              </If>
            </Case>
            <Case condition={isVipEver}>
              <div className={styles.subtitle}>已过期</div>
            </Case>
            <Default>
              <div className={styles.subtitle}>开通会员，下载商用源文件</div>
            </Default>
          </Switch>
        </div>
        <Switch>
          <Case condition={!isVip}>
            <Link href={isVipEver ? "/me/right" : "/vip/info"} passHref>
              <a className={styles.link} onClick={(e) => e.stopPropagation()}>
                <div
                  className={styles.text}
                  data-gd-click="button_click"
                  data-button-name={isVipEver ? "会员中心" : "立即开通"}
                >
                  {isVipEver ? "会员中心" : "立即开通"}
                </div>
                <HBIcon className={styles.icon} type="ic_chevron_right" />
              </a>
            </Link>
          </Case>
          <Default>
            <Link href="/me/right">
              <a
                className={styles.link}
                onClick={(evt) => evt.stopPropagation()}
              >
                <div
                  className={styles.text}
                  data-gd-click="button_click"
                  data-button-name="会员中心"
                >
                  会员中心
                </div>
                <HBIcon className={styles.icon} type="ic_chevron_right" />
              </a>
            </Link>
          </Default>
        </Switch>
      </div>
      <div className={styles.vipBottom}>
        <Link href={"/me/download"}>
          <a
            onClick={(e) => e.stopPropagation()}
            data-gd-click="button_click"
            data-button-name="我的下载"
          >
            <div>我的下载</div>
          </a>
        </Link>
        <Link href={"/me/coupon"}>
          <a
            onClick={onCouponClick}
            data-gd-click="button_click"
            data-button-name="优惠券"
          >
            <div>优惠券</div>
            {couponTip && <span className={styles.redTip}></span>}
          </a>
        </Link>
        <Link href={"/me/order"}>
          <a
            onClick={onOrderClick}
            data-gd-click="button_click"
            data-button-name="订单发票"
          >
            <div>订单发票</div>
            {orderTip && <span className={styles.redTip}></span>}
          </a>
        </Link>
      </div>
    </div>
  );
}
